import { Typography } from 'antd';
import { FC, useState } from 'react';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { IGenericBarItem } from '../../../types';
import {
  computeBarChartLayout,
  IBarChartDimensions,
} from '../../../utils/computeBarChartLayout';

const { Text } = Typography;

const DIMENSIONS: IBarChartDimensions = {
  svgWidth: 580,
  svgHeight: 160,
  padLeft: 38,
  padRight: 8,
  padTop: 12,
  padBottom: 32,
  gap: 4,
  tickCount: 4,
};

export const GenericBarChart: FC<{
  items: IGenericBarItem[];
  valueSuffix?: string;
}> = ({ items, valueSuffix = '%' }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  const { svgWidth, svgHeight, padLeft, padRight, padBottom } = DIMENSIONS;
  const { toY, ticks, bars, yearFooter } = computeBarChartLayout(
    items,
    DIMENSIONS
  );

  const stripYear = (label: string) =>
    label.replace(/\s+\d{4}$/, '').replace(/^\d{4}$/, '');

  return (
    <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        style={{ display: 'block', width: '100%', height: svgHeight }}
      >
        {ticks.map((tick) => {
          const y = toY(tick);
          return (
            <g key={tick}>
              <line
                x1={padLeft}
                y1={y}
                x2={svgWidth - padRight}
                y2={y}
                stroke={
                  tick === 0
                    ? BRAND_COLORS.secondary.lightSage
                    : BRAND_COLORS.secondary.steelBlue
                }
                strokeWidth={tick === 0 ? 1.5 : 0.8}
                strokeDasharray={tick === 0 ? undefined : '3 3'}
              />
              <text
                x={padLeft - 4}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill={BRAND_COLORS.secondary.lightGray}
              >
                {tick}
                {valueSuffix}
              </text>
            </g>
          );
        })}

        {items.map((item, i) => {
          const { x, y, width, height, rawHeight, isPositive } = bars[i];
          const isHov = hovered === i;

          return (
            <g key={item.label}>
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={item.color}
                opacity={isHov ? 1 : 0.82}
                rx={2}
                style={{ cursor: 'pointer', transition: 'opacity 0.1s' }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              />
              <text
                x={x + width / 2}
                y={svgHeight - padBottom + 12}
                textAnchor="middle"
                fontSize={11}
                fill={isHov ? '#ffffff' : BRAND_COLORS.secondary.lightGray}
                style={{ userSelect: 'none' }}
              >
                {stripYear(item.label)}
              </text>
              {rawHeight > 10 && (
                <text
                  x={x + width / 2}
                  y={isPositive ? y - 3 : y + rawHeight + 10}
                  textAnchor="middle"
                  fontSize={10}
                  fill={item.color}
                  fontWeight={700}
                >
                  {item.value}
                  {valueSuffix}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {yearFooter && (
        <div style={{ textAlign: 'center', marginTop: 2 }}>
          <Text
            style={{ color: BRAND_COLORS.secondary.lightGray, fontSize: 10 }}
          >
            {yearFooter}
          </Text>
        </div>
      )}

      {hovered !== null &&
        (() => {
          const item = items[hovered];
          const { x, width } = bars[hovered];
          const pct = (x + width / 2) / svgWidth;
          const tooltipW = 180;
          const leftPct = `clamp(0px, calc(${pct * 100}% - ${
            tooltipW / 2
          }px), calc(100% - ${tooltipW}px))`;
          return (
            <div
              style={{
                position: 'absolute',
                top: 4,
                left: leftPct,
                width: tooltipW,
                background: BRAND_COLORS.secondary.steelBlue,
                border: `1px solid ${BRAND_COLORS.primary.royalBlue}`,
                borderRadius: 6,
                padding: '8px 10px',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12,
                  marginBottom: 4,
                }}
              >
                {item.label}
              </div>
              <div style={{ color: item.color, fontSize: 13, fontWeight: 700 }}>
                {item.value}
                {valueSuffix}
              </div>
              {item.tooltipLines.map((line, li) => (
                <div
                  key={li}
                  style={{
                    color: BRAND_COLORS.secondary.lightSage,
                    fontSize: 11,
                    marginTop: li === 0 ? 4 : 2,
                  }}
                >
                  {line}
                </div>
              ))}
            </div>
          );
        })()}
    </div>
  );
};
